/*
 * 自定义代码块 schema 定义
 *
 * ⚠️ 这些 schema 必须与 XHeyClaw-Web 的后端输出格式严格对齐：
 *   - components/photo-list-block.tsx
 *   - components/file-list-block.tsx
 *   - components/task-status-block.tsx
 *   - components/metrics-block.tsx
 *   - components/echarts-block.tsx
 *   - components/verify-result-block.tsx
 *   - components/cron-card-block.tsx
 *   - components/skill-card-block.tsx
 *
 * 后端发的内容形如：
 *   ```photo-list
 *   {"photos": [{"url": "...", "time": "2025-10-26 11:42"}]}
 *   ```
 */

// ─── photo-list ──────────────────────────────
export interface PhotoItem {
  url: string
  thumbnail?: string
  small_url?: string
  time?: string
  address?: string
  user_name?: string
  media_type?: string
  title?: string
}

export interface PhotoListData {
  title?: string
  photos: PhotoItem[]
  has_more?: boolean
}

// ─── file-list ───────────────────────────────

/** 内联预览表格的单元格：纯文本、链接，或从 preview_url 解析出的图片缩略图 */
export type FilePreviewCell =
  | string
  | { text: string; href: string }
  | { type: 'image'; src: string; text?: string }

export interface FileItem {
  name: string
  url: string
  /** 文件大小友好字符串，如 "1.2MB" */
  size?: string
  type?: string
  desc?: string
  /** 裁剪后的预览文件 url，点"预览"开全屏时用（完整下载仍走 url） */
  preview_url?: string
  /** 原始总行数，卡片显示 "X 条记录" */
  total_rows?: number
  /** 原始总列数，卡片显示 "Y 列" */
  total_cols?: number
  /** 预览是否为截断子集（行或列被裁），为 true 时全屏顶部提示"仅部分数据" */
  truncated?: boolean
}

export interface FileListData {
  title?: string
  files: FileItem[]
}

// ─── task-status ─────────────────────────────
export type TaskStatus = 'done' | 'running' | 'pending' | 'error'

export interface TaskStep {
  label: string
  status: TaskStatus
  detail?: string
}

export interface TaskStatusData {
  title: string
  steps: TaskStep[]
}

// ─── metrics ─────────────────────────────────
export type MetricColor = 'green' | 'red' | 'yellow' | 'blue' | 'default'

export interface MetricItem {
  label: string
  value: string
  sub?: string
  color?: MetricColor
}

// metrics 整体是一个数组：MetricItem[]

// ─── echarts ─────────────────────────────────
// echarts 内容直接是 ECharts option，不再包一层

// ─── verify-result ───────────────────────────
export interface VerifyResultItem {
  photo_url: string
  verified: boolean
  status_code: number
  status_msg: string
  photo_time?: string
  photo_address?: string
  anti_fake_code?: string
  fail_reason?: string
}

export interface VerifyData {
  status: string
  total: number
  verified_count: number
  failed_count: number
  results: VerifyResultItem[]
}

// ─── cron-card ───────────────────────────────
export type CronStatus =
  | 'created'
  | 'updated'
  | 'enabled'
  | 'disabled'
  | 'deleted'

export interface CronCardData {
  name: string
  cron_expr: string
  status: CronStatus
  delivery?: string
  next_run?: string
  schedule_preview?: any
}

// ─── skill-card ──────────────────────────────
export type SkillStatus = 'created' | 'updated' | 'disabled' | 'deleted'

export interface SkillCardData {
  id?: string
  name: string
  display_name: string
  description?: string
  icon?: string
  category?: string
  status: SkillStatus
}

// ─── image-gallery ───────────────────────────
export type ImageGalleryItem = string | { url: string; thumbnail?: string }
// 整体是数组：ImageGalleryItem[]

// ─── 块名注册表 ──────────────────────────────
export const CUSTOM_BLOCK_LANGS = [
  'mermaid',
  'echarts',
  'metrics',
  'verify-result',
  'photo-list',
  'image-gallery',
  'file-list',
  'task-status',
  'cron-card',
  'skill-card'
] as const

export type CustomBlockLang = (typeof CUSTOM_BLOCK_LANGS)[number]

/** 安全 JSON 解析 */
export function tryParseJson<T = any>(code: string): T | null {
  try {
    return JSON.parse(code.trim()) as T
  } catch {
    return null
  }
}
